import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { ReportIssueService } from '../report-issue/report-issue.service';
import { DeviceLockManagementModalComponent } from '../../components/device-lock-management-modal/device-lock-management-modal.component';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import {
    ConcurrentDataSyncError,
    DEVICE_LOCK_CLIENTID,
    DEVICE_LOCK_CLIENTID_CREATED_AT,
    DEVICE_LOCK_KEY,
    DeviceLockAcquireRequestBody,
    DeviceLockAcquireResponse,
    DeviceLockFingerprint,
    DeviceLockHeldError,
    DeviceLockStateResponse,
    EMPTY_DEVICE_LOCK,
    getOrCreateClientId,
    INITIAL_LOCK_STATE,
    loadLegacyLocalLock,
    loadLocalLock,
    loadSyncedAt,
    LockState,
    LockValue,
    lockValueToState,
    remoteHolderToLockValue,
    removeLegacyLocalLock,
    saveLocalLock,
    saveSyncedAt,
    wasClientIdGeneratedThisSession,
} from './device.utils';
import { catchError, distinctUntilChanged, filter, map, pairwise, switchMap, take, tap } from 'rxjs/operators';
import { LogInstanceToken, PaceInsightsService } from '@evorait/pace-logging';
import { safeStringify } from '@evorait/pace-components';
import { UAParser } from 'ua-parser-js';
import { BsModalService } from 'ngx-bootstrap/modal';
import { detectIncognito } from 'detectincognitojs';
import { EnvironmentService } from '@evorait/pace-environment';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';

@Injectable({
    providedIn: 'root',
})
export class DeviceLockService implements InitializableInterface {
    private readonly log = inject(LogInstanceToken);
    private readonly http = inject(HttpClient);
    private readonly concurrentUsageService = inject(ConcurrentUsageService);
    private readonly connectivityService = inject(ConnectivityService);
    private readonly paceInsights = inject(PaceInsightsService);
    private readonly modalService = inject(BsModalService);
    private readonly configService = inject(ConfigInstanceToken);
    private readonly reportIssueService = inject(ReportIssueService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly clientManager = inject(ClientManagerInstanceToken);

    private readonly lockState = new BehaviorSubject<LockState>(INITIAL_LOCK_STATE);
    private readonly localFingerPrint: DeviceLockFingerprint;

    public isInitialized = false;

    private lockModalOpen = false;

    private readonly isReleaseLockInProgressSubject = new BehaviorSubject(false);

    public readonly lockState$ = this.lockState
        .asObservable()
        .pipe(
            distinctUntilChanged(
                (a, b) =>
                    a.ownedByMe === b.ownedByMe &&
                    a.ownerFingerprint?.clientId === b.ownerFingerprint?.clientId &&
                    a.acquiredAt === b.acquiredAt,
            ),
        );

    /**
     * Emits every time this client loses ownership of the lock, either by releasing it or by
     * another device taking it over. The PACE session behind it is over at that point, so
     * everything that outlives a single screen - above all scheduled timers - has to be stopped.
     *
     * Only a real transition emits: a client that never held the lock never fires, which keeps
     * the initial `ownedByMe: false` state from being mistaken for a release.
     */
    public readonly lockReleased$: Observable<void> = this.lockState$.pipe(
        pairwise(),
        filter(([previous, current]) => previous.ownedByMe && !current.ownedByMe),
        map(() => undefined),
    );

    public constructor() {
        const { os, device, browser } = new UAParser().getResult();
        this.localFingerPrint = {
            os: os.name,
            vendor: device.vendor,
            model: device.model,
            browser: browser.name,
            clientId: this.getClientId(),
            isPrivateBrowser: false,
            standalone: this.clientManager.isPacePWAStandalone(),
        };
    }

    public init(): Observable<unknown> {
        this.trackDeviceIdOrigin();

        window.addEventListener('storage', (event) => {
            if (event.key === DEVICE_LOCK_KEY) {
                const localValue = loadLocalLock();
                const localState = lockValueToState(localValue, this.deviceFingerprint);
                this.paceInsights.trackEvent('DEVICE_LOCK_UPDATE_AFTER_LOCALSTORAGE_EVENT', {
                    ...localState,
                    localId: this.deviceFingerprint,
                });
                this.log.info(
                    this.constructor.name + ': init storage event listener: lock changed. reading lock locally',
                    localState,
                );
                this.lockState.next(localState);

                this.log.info('storage event on lock state.', {
                    key: event.key,
                    newValue: event.newValue,
                    oldValue: event.oldValue,
                    url: event.url,
                });
                if (!localState.ownedByMe && !this.lockModalOpen) {
                    this.log.info(
                        'lock state changed in local storage, but is not owned by me anymore. need to show lock screen',
                    );
                    this.showLockModalIfNotOpen();
                }
            }
        });

        return this.setPrivateSessionState().pipe(
            switchMap(() => this.connectivityService.isOnline$),
            take(1), // connectivityService.isOnline$ is a not-completing stream and can retrigger subsequent elements in this pipeline.
            switchMap((online) => {
                if (!online) {
                    const localValue = loadLocalLock() ?? this.takeOverPreviousAzureLock();
                    if (!localValue && loadLegacyLocalLock()) {
                        // First start after the update: the carried-over lock is only known once the
                        // backend has answered, and guessing could replace unsynced local data.
                        this.paceInsights.trackEvent('DEVICE_LOCK_OFFLINE_LEGACY_LOCK', {
                            localId: this.deviceFingerprint,
                        });
                        this.log.warn(this.constructor.name + ': init: offline with only a legacy device lock');
                        throw new ConcurrentDataSyncError(
                            'Device lock migration incomplete. An internet connection is required to finish it.',
                        );
                    }
                    const localState = lockValueToState(localValue, this.deviceFingerprint);
                    this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_WO_INTERNET', {
                        ...localState,
                        localId: this.deviceFingerprint,
                    });
                    this.log.info(this.constructor.name + ': init: device is offline, read lock locally', localState);
                    this.lockState.next(localState);

                    if (localState.ownedByMe && loadSyncedAt() !== localState.acquiredAt) {
                        // The lock was acquired but the concurrent-usage download never completed
                        // (e.g. page refresh mid-download). Starting on stale data could overwrite
                        // the previous device's uploads, so this needs connectivity first.
                        this.paceInsights.trackEvent('DEVICE_LOCK_OFFLINE_SYNC_INCOMPLETE', {
                            localId: this.deviceFingerprint,
                        });
                        this.log.warn(
                            this.constructor.name +
                                ': init: offline, but the initial concurrent data sync of this' +
                                ' lock acquisition is incomplete',
                        );
                        throw new ConcurrentDataSyncError(
                            'Concurrent data sync incomplete. An internet connection is required to finish it.',
                        );
                    }

                    return of(!!localState?.ownedByMe);
                }

                // online must revalidate
                return this.tryAcquireLockAtInit();
            }),
            map((haveLock) => {
                this.paceInsights.trackEvent('DEVICE_LOCK_INIT_RESULTS', {
                    haveLock,
                    localId: this.deviceFingerprint,
                });

                if (!haveLock) {
                    this.log.warn(this.constructor.name + ': init: failed - do not have the device lock!');
                    throw new Error('This device does not have the device lock.');
                }
                this.isInitialized = true;
            }),
        );
    }

    public setLockModalOpen(open: boolean): void {
        this.lockModalOpen = open;
    }

    public updateReleaseLockStatus(isReleaseLockInProgress: boolean): void {
        this.isReleaseLockInProgressSubject.next(isReleaseLockInProgress);
    }

    /** Whether the device lock management screen is currently open. */
    public isLockModalOpen(): boolean {
        return this.lockModalOpen;
    }

    /**
     * Acquire must run before any other backend call: every feature endpoint (including the
     * SAP proxy used by ConcurrentUsageService) sits behind deviceLockMiddleware and would be
     * rejected with 423 while another device holds the lock. The concurrent-usage download
     * itself runs as a later init step (attachConcurrentDataSync): it stores data encrypted
     * and therefore needs the encryption service, which is initialized after the lock check.
     */
    private tryAcquireLockAtInit(): Observable<boolean> {
        return this.acquire();
    }

    /**
     * Runs the concurrent-usage download exactly once per lock acquisition.
     *
     * The lock's acquiredAt acts as the transaction id: a persisted marker records which
     * acquisition has completed its download. An interrupted download (e.g. page refresh)
     * leaves the marker unset and is retried on the next initialization, while an already
     * synced acquisition is skipped so local not-yet-uploaded concurrent data survives.
     */
    public ensureConcurrentDataSynced(): Observable<unknown> {
        const state = this.lockState.getValue();
        if (!state.ownedByMe || !state.acquiredAt) {
            this.log.warn(this.constructor.name + ': ensureConcurrentDataSynced: called without owning the lock');
            return of(undefined);
        }

        const acquiredAt = state.acquiredAt;
        if (loadSyncedAt() === acquiredAt) {
            this.log.debug(this.constructor.name + ': ensureConcurrentDataSynced: acquisition already synced');
            return of(undefined);
        }

        return this.concurrentUsageService.downloadAll().pipe(
            tap(() => {
                saveSyncedAt(acquiredAt);
                this.log.debug(
                    this.constructor.name + ': ensureConcurrentDataSynced: download completed, acquisition marked',
                );
            }),
            catchError((error: unknown) => {
                // the lock is owned — a failing download must not surface as a lock
                // ownership problem (that would loop the user through the lock screen)
                this.log.error(
                    this.constructor.name + ': ensureConcurrentDataSynced: download failed',
                    safeStringify(error),
                );
                return throwError(
                    () =>
                        new ConcurrentDataSyncError('Concurrent data download failed after acquiring the device lock.'),
                );
            }),
        );
    }

    /**
     * Tries to acquire the lock for the current client via the device-lock API.
     *
     * The backend creates the lock if none exists and refreshes it when this device already
     * holds it (idempotent). A 423 response means another device holds the lock; its identity
     * from the response body is emitted through `lockState$`. A lock carried over from the pre-Azure
     * client is adopted as already synced (see `continuesLegacyLock`).
     *
     * @returns Observable<boolean> that emits:
     * - `true`  if the lock is (after this call) owned by the current client
     * - `false` if the lock is owned by another client
     */
    public acquire(): Observable<boolean> {
        this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_ATTEMPT', { localId: this.deviceFingerprint });
        this.log.debug(this.constructor.name + ': acquire: attempting to acquire device lock');

        const url = `${this.environmentService.apiBase}${ENDPOINTS.DeviceLockAcquire}`;

        return this.http.post<DeviceLockAcquireResponse>(url, this.acquireRequestBody()).pipe(
            map((response) => {
                const acquiredAt = response.acquiredAt ?? new Date().toISOString();
                const legacyLock = loadLegacyLocalLock();
                if (legacyLock && this.continuesLegacyLock(legacyLock, response, acquiredAt)) {
                    // Must land before the legacy key is gone: a refresh in between would otherwise
                    // download over the local data that is newer than anything on the backend.
                    saveSyncedAt(acquiredAt);
                    this.paceInsights.trackEvent('DEVICE_LOCK_LEGACY_ADOPTED', {
                        acquiredAt,
                        localId: this.deviceFingerprint,
                    });
                }
                this.applyOwnLock(acquiredAt);
                if (legacyLock) {
                    removeLegacyLocalLock();
                }
                this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_RESULT', {
                    success: true,
                    localId: this.deviceFingerprint,
                });
                this.log.debug(this.constructor.name + ': acquire: successfully acquired lock');
                return true;
            }),
            catchError((error: unknown) => {
                if (error instanceof HttpErrorResponse && error.status === 423) {
                    removeLegacyLocalLock();
                    const state = this.applyForeignLock(error.error as DeviceLockHeldError);
                    this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_FAILED', {
                        ...state,
                        localId: this.deviceFingerprint,
                    });
                    this.log.warn(this.constructor.name + ': acquire: failed - locked by other device!', {
                        ...state,
                        localId: this.deviceFingerprint,
                    });
                    return of(false);
                }

                this.paceInsights.trackEvent('DEVICE_LOCK_ACQUIRE_RESULT', {
                    success: false,
                    localId: this.deviceFingerprint,
                });
                this.log.error(this.constructor.name + ': acquire: failed to acquire lock', safeStringify(error));
                return throwError(() => error);
            }),
        );
    }

    /**
     * Attempts to release the currently held lock for this client.
     *
     * The backend only accepts the release from the device holding the lock.
     *
     * @returns Observable<boolean> that emits:
     * - `true` if the lock was successfully released.
     * - `false` if this client did not own the lock.
     */
    public release(): Observable<boolean> {
        this.log.debug(this.constructor.name + ': release: attempting to release device lock');
        return this.refreshState().pipe(
            switchMap((current) => {
                if (!current?.ownedByMe) {
                    // we're not the owner and won't overwrite!
                    this.log.warn(this.constructor.name + ': release: cannot release, not owned by me', current);
                    return of(false);
                }

                const url = `${this.environmentService.apiBase}${ENDPOINTS.DeviceLockRelease}`;
                return this.http.delete<{ released: boolean }>(url).pipe(
                    map(() => {
                        this.applyLockValue(EMPTY_DEVICE_LOCK);
                        saveSyncedAt(null);
                        return true;
                    }),
                );
            }),
        );
    }

    /**
     * Refreshes and emits the current lock state.
     *
     * Reads the lock from remote and maps it into a `LockState` object.
     * The resulting state will be emitted through  `lockState$`.
     *
     * @returns Observable<LockState>  The updated lock state.
     */
    public refreshState(): Observable<LockState> {
        return this.readRemoteLock().pipe(
            map((v) => lockValueToState(v, this.deviceFingerprint)),
            tap((state) => this.lockState.next(state)),
        );
    }

    /**
     * Entry point for the 423 interceptor: a backend response told us the lock is held by
     * another device. Updates the local state from the error payload and opens the lock screen.
     *
     * @param held
     */
    public handleRemoteLockLoss(held: DeviceLockHeldError | undefined): void {
        const owner = held?.lock
            ? remoteHolderToLockValue(held.lock.deviceId, held.lock.device, held.lock.acquiredAt ?? null)
            : null;

        if (owner?.fingerprint?.clientId === this.deviceFingerprint.clientId) {
            // defensive: the server says the lock is ours after all — nothing to handle
            return;
        }

        this.paceInsights.trackEvent('DEVICE_LOCK_LOST_DETECTED_VIA_423', {
            owner: owner?.fingerprint,
            localId: this.deviceFingerprint,
        });
        this.log.warn(this.constructor.name + ': handleRemoteLockLoss: lock is held by another device', owner);

        this.applyLockValue(owner);
        this.showLockModalIfNotOpen();
    }

    /**
     * Emits whether this session generated a fresh device id or re-used the persisted one.
     * Fired from init() instead of the constructor so that component tests constructing the
     * service with minimal mocks do not need a PaceInsightsService stub.
     */
    private trackDeviceIdOrigin(): void {
        const generated = wasClientIdGeneratedThisSession();
        this.paceInsights.trackEvent(generated ? 'DEVICE_ID_GENERATED' : 'DEVICE_ID_REUSED', {
            clientId: this.deviceFingerprint.clientId,
            createdAtRaw: localStorage.getItem(DEVICE_LOCK_CLIENTID_CREATED_AT),
        });
    }

    private getClientId(): string {
        const now = Date.now();

        const storedClientId = localStorage.getItem(DEVICE_LOCK_CLIENTID);
        const createdAtRaw = localStorage.getItem(DEVICE_LOCK_CLIENTID_CREATED_AT);

        const createdAt = createdAtRaw ? Number(createdAtRaw) : null;

        if (!storedClientId && createdAtRaw) {
            this.log.error(
                'STORAGE CORRUPTED? found a client id creating date, but not the client ID. that must not happen!',
                { storedClientId, createdAtRaw, createdAt },
            );
        }

        // Creation and persistence live in the shared util so the HTTP interceptor
        // resolves the same ID without depending on this service.
        const clientId = getOrCreateClientId();

        // The util memoizes whether this session generated the id, because the interceptor
        // may have resolved it before this service was constructed.
        const generated = wasClientIdGeneratedThisSession();

        detectIncognito()
            .then((incognito) => {
                const ageMs = createdAt ? now - createdAt : null;
                const ageMinutes = ageMs !== null ? Math.floor(ageMs / 60000) : null;
                const ageHours = ageMs !== null ? Math.floor(ageMs / (60 * 60 * 1000)) : null;
                const ageDays = ageMs !== null ? Math.floor(ageMs / (24 * 60 * 60 * 1000)) : null;

                this.paceInsights.trackEvent('DEVICE_LOCK_CLIENT_ID', {
                    clientId,
                    createdAt,
                    createdAtRaw,
                    incognito,
                    // storedClientId is unreliable here: the interceptor may have created the
                    // id earlier in this session, so the session-scoped tracking decides.
                    found: !generated,
                    userAgent: window.navigator.userAgent,
                    deviceFingerprint: this.deviceFingerprint,
                    age: {
                        humanReadable: `${ageDays}d ${ageHours}h ${ageMinutes}m`,
                        ageDays,
                        ageHours,
                        ageMinutes,
                        ageMs,
                    },
                });
            })
            .catch((error) =>
                this.log.error('failed to detect incognito mode for tracking client id event', JSON.stringify(error)),
            );
        return clientId;
    }

    public get deviceFingerprint(): DeviceLockFingerprint {
        return this.localFingerPrint;
    }

    private readRemoteLock(): Observable<LockValue | null> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.DeviceLockGet}`;
        return this.http.get<DeviceLockStateResponse>(url).pipe(
            map((state) =>
                state.locked && state.deviceId
                    ? remoteHolderToLockValue(state.deviceId, state.device, state.acquiredAt ?? null)
                    : null,
            ),
            tap((lock) => {
                this.log.debug(this.constructor.name + ': readRemoteLock: read lock from device-lock api', lock);
                saveLocalLock(lock);
            }),
        );
    }

    private acquireRequestBody(): DeviceLockAcquireRequestBody {
        const { os, vendor, model, browser, isPrivateBrowser } = this.localFingerPrint;
        return { os, vendor, model, browser, isPrivateBrowser };
    }

    /**
     * The first Azure client (6.0.0) stored its lock under what is now the legacy key. Such a lock is
     * recognisable by its synced marker, which the pre-Azure client never wrote; moving it to the new
     * key lets that user keep starting offline after the update.
     */
    private takeOverPreviousAzureLock(): LockValue | undefined {
        const legacyLock = loadLegacyLocalLock();
        const isPreviousAzureLock =
            !!legacyLock?.acquiredAt &&
            legacyLock.fingerprint?.clientId === this.deviceFingerprint.clientId &&
            loadSyncedAt() === legacyLock.acquiredAt;
        if (!legacyLock || !isPreviousAzureLock) {
            return undefined;
        }
        saveLocalLock(legacyLock);
        removeLegacyLocalLock();
        return legacyLock;
    }

    /**
     * Whether this acquisition is the Azure copy of the lock this browser held under the pre-Azure
     * client — its local data is then the newest there is and must survive the first check-in.
     */
    private continuesLegacyLock(
        legacyLock: LockValue,
        response: DeviceLockAcquireResponse,
        acquiredAt: string,
    ): boolean {
        return (
            response.migratedFromGateway === true &&
            legacyLock.fingerprint?.clientId === this.deviceFingerprint.clientId &&
            legacyLock.acquiredAt === acquiredAt
        );
    }

    // Persists and emits the lock as owned by this device.
    private applyOwnLock(acquiredAt: string): void {
        this.applyLockValue({ fingerprint: this.deviceFingerprint, acquiredAt });
    }

    // Persists and emits the foreign lock holder from a 423 error body. Returns the new state.
    private applyForeignLock(held: DeviceLockHeldError | undefined): LockState {
        const owner = held?.lock
            ? remoteHolderToLockValue(held.lock.deviceId, held.lock.device, held.lock.acquiredAt ?? null)
            : null;
        return this.applyLockValue(owner);
    }

    private applyLockValue(lock: LockValue | null): LockState {
        saveLocalLock(lock);
        const state = lockValueToState(lock, this.deviceFingerprint);
        this.lockState.next(state);
        return state;
    }

    private showLockModalIfNotOpen(): void {
        if (this.lockModalOpen) {
            return;
        }
        this.modalService.show(DeviceLockManagementModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            ariaLabelledBy: 'app-notification-modal-title',
            keyboard: false,
            ignoreBackdropClick: true,
            closeInterceptor: () => {
                return new Promise((resolve, reject) => {
                    this.log.warn("User want's to close device lock screen, which is not allowed");
                    reject();
                });
            },
        });
    }

    /**
     * Takes over the lock from another device via force-acquire.
     *
     * Kept under its historical name: previously this cleared the foreign lock so the caller
     * could acquire afterwards. The v1 API does both in one atomic force-acquire call, so a
     * subsequent acquire simply confirms ownership.
     */
    public forceRemoveLock(): Observable<unknown> {
        if (window.location.hostname === 'localhost') {
            return this.forceAcquireRemote();
        } else {
            return this.refreshState().pipe(
                switchMap(() => this.lockState$),
                take(1),
                filter((state) => this.isDeviceLockAllowedToBeRemoved(state)),
                switchMap((state) => this.reportIssueService.logForceDeviceUnlock(state, this.deviceFingerprint)),
                switchMap(() => this.forceAcquireRemote()),
            );
        }
    }

    private forceAcquireRemote(): Observable<boolean> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.DeviceLockForceAcquire}`;
        return this.http.post<DeviceLockAcquireResponse>(url, this.acquireRequestBody()).pipe(
            map((response) => {
                this.applyOwnLock(response.acquiredAt ?? new Date().toISOString());
                this.log.info(this.constructor.name + ': forceAcquireRemote: lock forcefully acquired');
                const clientId = getOrCreateClientId();
                detectIncognito()
                    .then((incognito) => {
                        this.paceInsights.trackEvent('FORCE_ACQUIRE', {
                            clientId,
                            incognito,
                            userAgent: window.navigator.userAgent,
                            deviceFingerprint: this.deviceFingerprint,
                        });
                    })
                    .catch((error) =>
                        this.log.error(
                            'failed to detect incognito mode for force acquire event',
                            JSON.stringify(error),
                        ),
                    );
                return true;
            }),
        );
    }

    /**
     * If the client ID has changed, but the device information is the same, it is likely that the user has cleared the
     * browser cache, locking themselves out. This function checks for this state.
     *
     * @returns boolean
     * @param state : LockState containing info about the locking device
     */
    public isDeviceLockAllowedToBeRemoved(state: LockState): boolean {
        /*
            After discussion with Lukas on 18th AUG:
            We allow Force Unlock always for now. Maybe we change it later back.
            Accessing state here is only for linting. I am expecting more changes
            and want to save time now. So I leave it like this for now.
        */
        return true || !!state.ownerFingerprint;
        /*
        return (
            state.ownerFingerprint?.clientId !== this.deviceFingerprint.clientId &&
            !!state.ownerFingerprint &&
            isDeterministicFingerprintEqual(state.ownerFingerprint, this.deviceFingerprint)
        );
        */
    }

    private setPrivateSessionState(): Observable<boolean> {
        return from(detectIncognito()).pipe(
            map((result) => {
                this.log.info(this.constructor.name + ': setPrivateSessionState', { result });
                this.localFingerPrint.isPrivateBrowser = result.isPrivate;
                return result.isPrivate;
            }),
            catchError((error) => {
                this.log.error(
                    this.constructor.name + ': setPrivateSessionState: Private Browser detection tool failed',
                    { error: safeStringify(error) },
                );
                return of(false);
            }),
        );
    }

    public isLockActive(): boolean {
        return this.lockState.value.ownedByMe && !this.isReleaseLockInProgressSubject.value;
    }
}
